/**
 * The client activity audit (BUILD_PLAN.md section 13.5; ADR 0123): every
 * activity for one client over the days, across the techs, in order. Each is
 * as the day view shows it: its day and times in the tech's own timezone, the
 * source, what it was and its time, with its block's ticket and status, and
 * whether that ticket is still open (ADR 0185). A
 * HIPAA client's show only what the day view does, each marked "Details
 * withheld" (section 14.4).
 *
 * The audit is for one client: the filters' Clients must name exactly one. A
 * tech's is of their own activities (section 6.2); the API scopes it.
 * "Download CSV" saves the same rows.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useId } from "react";

import type { ClientAudit, ClientAuditRow } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Badge, HipaaBadge } from "../../components/ui/Badge";
import { Panel } from "../../components/ui/Panel";
import { SourceTile } from "../../components/ui/SourceTile";
import { formatTimeIn } from "../../lib/dates";
import { saveBlob } from "../../lib/download";
import { formatDuration } from "../../lib/durations";
import { possessive } from "../../lib/possessive";
import { laneOf, sourceLabel } from "../../lib/sources";
import { count, errorMessage, pillButton, retryOnce } from "./common";
import { useReportFilters } from "./filters";
import { DayLink, StatusBadge, TicketCell } from "./ReportCells";

// As the day view's activity rows say a call's direction.
const DIRECTIONS: Partial<Record<ClientAuditRow["direction"], string>> = { inbound: "Inbound", outbound: "Outbound" };

function techName(row: ClientAuditRow): string {
  return row.tech.display_name ?? row.tech.email ?? "Unnamed user";
}

function when(row: ClientAuditRow): string {
  const zone = row.tech.timezone;
  if (row.ended_at === null) {
    // As the day view's card for it says.
    return row.source === "autotask_time" ? "No times in Autotask" : formatTimeIn(row.started_at, zone);
  }
  return `${formatTimeIn(row.started_at, zone)}–${formatTimeIn(row.ended_at, zone)}`;
}

function Row({ row }: { row: ClientAuditRow }) {
  const lane = laneOf(row.source);
  const direction = DIRECTIONS[row.direction];
  return (
    <tr className="border-t border-line align-top">
      <td className="py-3 pr-3 whitespace-nowrap">
        <DayLink day={row.day} userId={row.tech.id} tech={techName(row)} />
      </td>
      <td className="py-3 pr-3 font-mono text-xs whitespace-nowrap">{when(row)}</td>
      <td className="py-3 pr-3">
        <span className="flex flex-col gap-0.5">
          <span className="font-medium">{techName(row)}</span>
          <span className="text-xs text-muted">{row.tech.timezone}</span>
        </span>
      </td>
      <td className="py-3 pr-3">
        {lane ? <SourceTile lane={lane} /> : <span className="text-[13px] text-muted">{sourceLabel(row.source)}</span>}
      </td>
      <td className="py-3 pr-3">
        <span className="flex flex-col gap-1">
          <span className="break-words">{row.details}</span>
          {direction ? <span className="text-xs text-muted">{direction}</span> : null}
          {row.details_withheld ? <Badge tone="hipaa">Details withheld</Badge> : null}
        </span>
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <span className="font-semibold tracking-[-0.02em]">{formatDuration(row.seconds)}</span>
        {row.not_counted !== null ? (
          <span className="block text-xs text-muted">{`not counted · ${row.not_counted}`}</span>
        ) : null}
      </td>
      <td className="py-3 pr-3 font-mono text-xs whitespace-nowrap">
        <TicketCell number={row.ticket_number} url={row.ticket_url} state={row.ticket_state} />
      </td>
      <td className="py-3">
        {row.status !== null && row.status_label !== null ? (
          <StatusBadge status={row.status} label={row.status_label} />
        ) : null}
      </td>
    </tr>
  );
}

function Activities({ audit }: { audit: ClientAudit }) {
  const headingId = useId();
  const { client, rows } = audit;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="flex items-center gap-2">
          <h3 id={headingId} className="text-base font-[650] tracking-[-0.015em]">
            {client.name}
          </h3>
          {client.is_hipaa ? <HipaaBadge /> : null}
        </span>
        <p className="text-[13px] text-muted">
          {[count(rows.length, "activity", "activities"), "Times in each tech's own timezone"].join(" · ")}
        </p>
      </div>
      {client.is_hipaa ? (
        <p className="text-[13px] text-muted">
          A HIPAA client: each activity shows only what the day view shows, and its other details are withheld.
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="rounded-row bg-panel-2 px-4 py-3 text-muted">No activity in these days.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <caption className="sr-only">{`${possessive(client.name)} activities`}</caption>
            <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
              <tr>
                <th scope="col" className="pb-2 pr-3">
                  Date
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Time
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Tech
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Source
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Details
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Duration
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Ticket
                </th>
                <th scope="col" className="pb-2">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row key={row.activity_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ClientAuditReport() {
  const { api, me } = useSession();
  const headingId = useId();
  const [filters] = useReportFilters();
  const oneClient = filters.clientIds.length === 1;
  const report = useQuery({
    queryKey: ["reports", "client-audit", filters],
    queryFn: () => api.getClientAudit(filters),
    retry: retryOnce,
    enabled: oneClient,
  });
  const download = useMutation({
    mutationFn: () => api.downloadClientAuditCsv(filters),
    onSuccess: (blob) => {
      saveBlob(blob, `client-audit-${filters.start}-to-${filters.end}.csv`);
    },
  });

  return (
    <Panel aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-lg font-semibold tracking-tight">
            Client activity audit
          </h2>
          <p className="text-[13.5px] text-muted">
            {me.role === "owner"
              ? "Every activity for one client, across the techs, as their day view shows it."
              : "Your activities for one client, as your day view shows them."}
          </p>
        </div>
        <button
          type="button"
          className={pillButton}
          disabled={!oneClient || !report.isSuccess || download.isPending}
          onClick={() => {
            download.mutate();
          }}
        >
          Download CSV
        </button>
      </div>
      {download.isError ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not downloaded: {errorMessage(download.error)}
        </p>
      ) : null}
      {!oneClient ? (
        <p className="rounded-row bg-panel-2 px-4 py-3 text-muted">
          {filters.clientIds.length === 0
            ? "Choose a client in the filters: the audit lists every activity for one client."
            : "The audit is for one client: remove all but one in the filters."}
        </p>
      ) : report.isPending ? (
        <p role="status" className="text-muted">
          Loading the audit…
        </p>
      ) : report.isError ? (
        <p role="alert" className="text-status-needs-client-fg">
          {errorMessage(report.error)}
        </p>
      ) : (
        <Activities audit={report.data} />
      )}
    </Panel>
  );
}
